import * as React from 'react'

import { useAuth } from '@/lib/auth/context'
import { env } from '@/lib/env'

/**
 * Pairing bridge for the Sprint browser extension.
 *
 * The extension's content script on this origin announces itself with a
 * `{ source: 'sprint-extension', type: 'ready' }` window message; we answer
 * (and proactively broadcast on every auth change) with the club session the
 * extension needs to call the API on the user's behalf. Logging out
 * broadcasts a null payload so the extension unpairs.
 *
 * Security: the message never leaves this window (`targetOrigin` is our own
 * origin) and carries only what the extension needs — the Sprint JWT and API
 * base URL. Any script able to listen here could already read the session
 * from localStorage, so this adds no new exposure surface.
 */

const CONSOLE_SOURCE = 'sprint-console'
const EXTENSION_SOURCE = 'sprint-extension'

export interface ExtensionSessionPayload {
  apiUrl: string
  accessToken: string
  clubname: string
  expiresAt: string
}

export function useExtensionBridge(): void {
  const { club } = useAuth()

  React.useEffect(() => {
    const payload: ExtensionSessionPayload | null = club
      ? {
          apiUrl: env.apiUrl,
          accessToken: club.accessToken,
          clubname: club.clubname,
          expiresAt: club.expiresAt,
        }
      : null

    const broadcast = () => {
      window.postMessage(
        { source: CONSOLE_SOURCE, type: 'session', payload },
        window.location.origin,
      )
    }

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) {
        return
      }
      const data = event.data as { source?: string; type?: string } | null
      if (data?.source === EXTENSION_SOURCE && data.type === 'ready') {
        broadcast()
      }
    }

    window.addEventListener('message', onMessage)
    broadcast()
    return () => window.removeEventListener('message', onMessage)
  }, [club])
}
